import { BdAddress } from './address.model';
import { AysClinicIdentifier } from './clinic-identifier';
import type { AysClinicSnIdentifier } from './clinic-sn-identifier';
import type { AysClinicType } from './clinic-type';

export interface AysClinicResponse {
  id?: AysClinicIdentifier;
  name?: string;
  nameOfOwner?: string;
  type?: string;
  size?: number;
  beds?: number;
  cmi?: number;
  address?: BdAddress;
  level?: { id: string; displayName: { [locale: string]: string } };
}

/**
 * Lean, country-neutral clinic identity returned by `ClinicReadService.get()`.
 *
 * This is the ONLY shape country-neutral consumers may rely on from `get()`
 * (verified for BDA-5742: no contract consumer reads `beds`/`cmi`/`size`/
 * `type`/`level`/`nameOfOwner`). The rich `AysClinic` still exists for the two
 * concrete consumers that need more — CH's `HtmClinic` (via the concrete
 * `ChClinicReadService`) and the export pipelines — and remains assignable to
 * this type, so those implementations keep returning their richer objects.
 */
export interface AysClinicSummary {
  id: AysClinicIdentifier;
  name: string;
  address: BdAddress;
}

export class AysClinic implements AysClinicResponse, AysClinicSummary {
  public static fromObj(obj: AysClinicResponse = {}): AysClinic {
    return new AysClinic(
      AysClinicIdentifier.fromObj(obj.id),
      obj.name,
      obj.nameOfOwner,
      BdAddress.fromObject(obj.address),
      obj.type as AysClinicType,
      obj.size,
      obj.beds,
      obj.cmi,
      obj.level
    );
  }

  constructor(
    public id: AysClinicIdentifier,
    public name: string,
    public nameOfOwner: string,
    public address: BdAddress,
    public type: AysClinicType,
    public size: number,
    public beds: number,
    public cmi: number,
    public level: { id: string; displayName: { [locale: string]: string } }
  ) {}
}

export interface AysClinicSearchResponseItem {
  id: AysClinicIdentifier;
  name: string;
  address: BdAddress;
  /** Number of beds — drives map label placement priority (BDA-5387). */
  beds?: number;
}

/**
 * SN-native counterpart of {@link AysClinicSummary}: the only shape
 * country-neutral consumers may rely on from `ClinicReadService.get()`.
 *
 * Identity is the opaque `clinicSn` carried by {@link AysClinicSnIdentifier} —
 * no `ik`/`sn` pair. The institution key is still reachable for DISPLAY through
 * `id.ik`, which is where the legacy backends also recover it from; see the
 * round-trip invariant on `ClinicReadService`.
 */
export interface AysClinicSnSummary {
  id: AysClinicSnIdentifier;
  name: string;
  address: BdAddress;
}

/**
 * A search/`getAll` row. Adds `beds`, which drives map label placement priority
 * (BDA-5387) and is absent from the single-clinic `get()` shape.
 */
export interface AysClinicSnSearchItem extends AysClinicSnSummary {
  beds?: number;
}

/**
 * SN-native counterpart of {@link AysClinicSearchPage}. `total` carries the same
 * meaning: the server-side match count, which equals `items.length` for the
 * backends that answer unpaginated.
 */
export interface AysClinicSnSearchPage {
  items: AysClinicSnSearchItem[];
  total: number;
}

/**
 * One page of clinic search results plus how many matches exist server-side.
 *
 * `total` exists so a caller can tell "these are all the matches" apart from
 * "these are the first N of many" — without it, a bounded result set is
 * indistinguishable from a complete one and the UI has to present an arbitrary
 * slice as if it were the answer.
 *
 * Backends that answer a search unpaginated (DE-legacy `GET /clinics/?q=`, and
 * CH through it) return the full set, so they report `total === items.length`.
 * That is a true statement for them, not a placeholder: there is nothing beyond
 * what was handed back. Only the SN-native `/clinics/search` paginates, and it
 * reports the real server-side count.
 */
export interface AysClinicSearchPage {
  items: AysClinicSearchResponseItem[];
  total: number;
}

export interface AysClinicContainer {
  clinic: AysClinicSearchResponseItem;
  geometry?: AysClinicGeometry;
}

export interface AysClinicGeometry {
  distance?: number;
  point?: number[];
}

export interface AysClinicAvailableClinicLevels {
  levels: {
    [clinicLevel: string]: {
      id: string;
      displayName: { [locale: string]: string };
    };
  };
  information: {
    date: Date;
    information: { [language: string]: string };
  };
}
